// Plan copy. Source of truth: docs/v2-product-design.md §4.
import type { PlanLimits } from '../../types';

/** Where a seller updates the card on a Whop membership. */
export const WHOP_MEMBERSHIPS_URL = 'https://whop.com/@me/settings/memberships';

/** Whether this seller has to go Pro before starting another test. */
export function needsProForNewTest(plan: PlanLimits | null): boolean {
  return plan !== null && plan.freeTestUsed && !plan.hasProAccess;
}

/** Only a known trial is promised: the checkout has the final say. */
export function upgradeBody(trialAvailable: boolean | undefined): string {
  const price = trialAvailable === true ? '14 days free, then $29/mo' : '$29/mo';
  return `One test is free. Your results stay yours. Nothing you have running stops. More tests need Pro: ${price}.`;
}

export function upgradeButtonLabel(trialAvailable: boolean | undefined): string {
  return trialAvailable === true ? 'Start 14-day free trial' : 'Subscribe $29/mo';
}

export type PlanLineAction = 'upgrade' | 'update_card';

export interface PlanLine {
  text: string;
  action?: { kind: PlanLineAction; label: string };
}

function shortDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** Home's line about the plan, by state. Null while the plan is unknown. */
export function planLine(plan: PlanLimits | null): PlanLine | null {
  if (!plan) return null;
  switch (plan.status) {
    case 'active':
      return { text: "You're on Pro: run as many tests as you like." };
    case 'trialing':
      return {
        text: plan.trialEndsAt
          ? `Pro trial until ${shortDate(plan.trialEndsAt)}, then $29/mo.`
          : 'Pro trial, then $29/mo.',
      };
    case 'past_due':
      return {
        text: 'Your last Pro payment failed. Update your card on Whop to keep Pro.',
        action: { kind: 'update_card', label: 'Update card' },
      };
    case 'canceled':
      return plan.freeTestUsed
        ? {
            text: 'Your Pro plan has ended. Your tests and results are still here.',
            action: { kind: 'upgrade', label: 'Subscribe again' },
          }
        : { text: 'Your Pro plan has ended. You still have your free test.' };
    default:
      // The same line once the free test is used: Home never interrupts a seller
      // watching their first test. The upgrade prompt comes when they start another.
      return { text: 'Your first test is free. More tests need Pro.' };
  }
}
